import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Divider,
    Switch,
    Checkbox,
    Button,
    Typography
} from '@mui/material';
import { getClicks, blockIP, unblockIP, getBlockedIPs, deleteClicks } from '../../services/clicks';
import Filters from './Filters';
import Loading from './Loading';
import getOriginIcon from '../../utils/originTypes';
import MaterialIcon from 'material-icons-react';
import { useTranslation } from 'react-i18next';

function ClicksList({ user }) {

    const { t } = useTranslation();

    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [clicks, setClicks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); 
    const [selectedClicks, setSelectedClicks] = useState([]);
    const [blockedIPs, setBlockedIPs] = useState([]);

    const [startDate, setStartDate] = useState(() => {
        const today = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000); // 180 days ago
        return today.toISOString().split('T')[0]; 
    });
    const [endDate, setEndDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });

    const [page, setPage] = useState(0); // Pagination: current page
    const [rowsPerPage, setRowsPerPage] = useState(10); // Pagination: rows per page

    const loadClicks = async () => {
        setError(null);
        setIsLoading(true);
        try {
            const { clicks } = await getClicks(user._id, startDate, endDate);
            const { blockedIPs } = await getBlockedIPs(user._id)
            const listOfBlockedIPs = blockedIPs.map(ip => ip.ip);

            const groupedByIp = clicks.reduce((acc, click) => {
                const ip = click.ip?.data?.ip || t('unknown_ip');
                if (!acc[ip]) {
                    acc[ip] = { ...click, count: 0, accesses: [click.timestamp] };
                } else {
                    acc[ip].accesses.push(click.timestamp);
                }
                acc[ip].count += 1;
                return acc;
            }
            , {});

            // Convert the object back to an array
            const groupedByIpArray = Object.values(groupedByIp);
            setClicks(groupedByIpArray);
            setBlockedIPs(listOfBlockedIPs);
        } catch (error) {
            setError(t('.fetch_error'));
        } finally {
            setIsLoading(false);
            setLastUpdate(Date.now());
        }
    };

    useEffect(() => {
        loadClicks();
    }, []);

    // Handle Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelectClick = (clickId) => {
        const updatedClicks = selectedClicks.includes(clickId)
            ? selectedClicks.filter((id) => id !== clickId)
            : [...selectedClicks, clickId];

        setSelectedClicks(updatedClicks);
    }

    const handleDeleteClicks = async () => {
        setIsLoading(true);
        try {
            await deleteClicks(user._id, selectedClicks);
            setSelectedClicks([]);
            loadClicks();
        } catch (error) {
            setError(t('clicks.delete_error'));
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Box>
            <Filters
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                onReload={loadClicks}
                lastUpdate={lastUpdate}
            />

            <Divider />

            { selectedClicks .length > 0 && 
                <Box sx={{ padding: 1, display: 'flex', justifyContent: 'right' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            handleDeleteClicks();
                        }}
                    >
                        {t('clicks.delete_selected_clicks')}
                    </Button>
                </Box>
            }
            <TableContainer 
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    padding: 2
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{fontWeight: 'bold'}}>IP</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.last_access')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.first_access')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.visit_count')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.device')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.origin')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.type')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.country')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.region')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.status')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.is_blocked')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('clicks.select')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clicks
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) // Paginate data
                            .map((click) => {
                                const ipData = click.ip?.data
                                const isIPBlocked = blockedIPs.includes(ipData?.ip) ?? false
                                return (
                                    (
                                        <TableRow key={click._id}>
                                            <TableCell>{ipData?.ip || "N/A"}</TableCell>
                                            <TableCell>{click.accesses.at(-1)}</TableCell>
                                            <TableCell>{click.accesses.at(0)}</TableCell>
                                            <TableCell>{click.count}</TableCell>
                                            <TableCell>{click.device?.device?.type || "N/A"}</TableCell>
                                            <TableCell>{click.clickType}</TableCell>
                                            <TableCell>
                                                <MaterialIcon icon={getOriginIcon(click)} />
                                            </TableCell>
                                            <TableCell>{ipData?.location?.country?.emoji || "N/A"}</TableCell>
                                            <TableCell>{ipData?.location?.region?.name || "N/A"}</TableCell>
                                            <TableCell>{isIPBlocked ? "Bloqueado" : "Liberado"}</TableCell>
                                            <TableCell>
                                            <Switch
                                                checked={isIPBlocked}
                                                onChange={async () => {
                                                    isIPBlocked 
                                                        ? await unblockIP(ipData?.ip, user._id) 
                                                        : await blockIP(ipData?.ip, user._id)
                                                    loadClicks();
                                                }
                                                }
                                                inputProps={{ 'aria-label': 'controlled' }}
                                            />
                                            </TableCell>
                                            <TableCell>
                                            <Checkbox checked={selectedClicks.includes(click._id)} onChange={() => handleSelectClick(click._id)} />
                                            </TableCell>
                                        </TableRow>
                                    )
                                )
                            }
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Table Section for smaller screens */}
            <Box sx={{ 
                display: { xs: 'block', md: 'none' },
                padding: 2,
                overflowY: { xs: 'auto', sm: 'hidden' },
                maxHeight: { xs: '50vh', sm: 'none'},
            }}>
                {clicks
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((click) => {
                    const ipData = click.ip?.data;
                    const isIPBlocked = blockedIPs.includes(ipData?.ip) ?? false;

                    return (
                        <Box
                        key={click._id}
                        sx={{
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            mb: 2,
                            p: 2,
                            boxShadow: 1,
                            backgroundColor: '#fff',
                        }}
                        >
                        <Typography variant="subtitle2"><strong>IP:</strong> {ipData?.ip || "N/A"}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.last_access')}:</strong> {click.accesses.at(-1)}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.first_access')}:</strong> {click.accesses.at(0)}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.visit_count')}:</strong> {click.count}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.device')}:</strong> {click.device?.device?.type || "N/A"}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.origin')}:</strong> {click.clickType}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.type')}:</strong> <MaterialIcon icon={getOriginIcon(click)} /></Typography>
                        <Typography variant="body2"><strong>{t('clicks.country')}:</strong> {ipData?.location?.country?.emoji || "N/A"}</Typography>
                        <Typography variant="body2"><strong>{t('clicks.region')}:</strong> {ipData?.location?.region?.name || "N/A"}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Typography variant="body2"><strong>{t('clicks.status')}:</strong> {isIPBlocked ? t('clicks.status_blocked') : t('clicks.status_unblocked')}</Typography>
                                <Switch
                                    checked={isIPBlocked}
                                    onChange={async () => {
                                        isIPBlocked
                                        ? await unblockIP(ipData?.ip, user._id)
                                        : await blockIP(ipData?.ip, user._id);
                                        loadClicks();
                                    }}
                                />
                            </Box>
                            <Checkbox
                            checked={selectedClicks.includes(click._id)}
                            onChange={() => handleSelectClick(click._id)}
                            />
                        </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Pagination Controls */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={clicks.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        
        <Loading 
            text={t('loading_clicks')}
            isLoading={isLoading} 
        />
        </Box>
    );
}

export default ClicksList;